import { useRef, useState, type FormEvent } from "react";
import {
  OrganizationIntakeSchema,
  ORGANIZATION_TAXONOMY,
  type OrganizationIntake,
} from "@sourceenergy/sidekick-contracts";
import { Check, FileCheck2, ShieldAlert } from "lucide-react";
import { api } from "../lib/api";
import { useToast } from "./toast-context";

const CAPABILITIES = [
  {
    value: "VIEW",
    label: "View",
    description: "Read approved records",
  },
  {
    value: "CONTRIBUTE",
    label: "Contribute",
    description: "Submit evidence and updates",
  },
  {
    value: "PROPOSE",
    label: "Propose",
    description: "Draft work for review",
  },
  {
    value: "EXECUTE",
    label: "Execute",
    description: "Perform approved actions",
    elevated: true,
  },
  {
    value: "ADMINISTER",
    label: "Administer",
    description: "Manage bounded scopes",
    elevated: true,
  },
] as const;

const SAFE_DEFAULTS = new Set(["VIEW", "CONTRIBUTE", "PROPOSE"]);

export function OrganizationForm({
  token,
  onCreated,
}: {
  token: string;
  onCreated: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCapabilities, setSelectedCapabilities] = useState(
    () => new Set(SAFE_DEFAULTS),
  );
  const [primaryCategoryCode, setPrimaryCategoryCode] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  const toast = useToast();
  const elevatedRequested =
    selectedCapabilities.has("EXECUTE") ||
    selectedCapabilities.has("ADMINISTER");

  function updateCapability(value: string, checked: boolean) {
    setSelectedCapabilities((current) => {
      const next = new Set(current);
      if (checked) next.add(value);
      else next.delete(value);
      return next;
    });
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    const data = new FormData(event.currentTarget);
    const draft: OrganizationIntake = {
      legalName: String(data.get("legalName") ?? ""),
      organizationType: String(
        data.get("organizationType") ?? "OTHER",
      ) as OrganizationIntake["organizationType"],
      relationshipType: String(
        data.get("relationshipType") ?? "EXTERNAL_PARTNER",
      ) as OrganizationIntake["relationshipType"],
      primaryCategoryCode: String(
        data.get("primaryCategoryCode") ?? "",
      ) as OrganizationIntake["primaryCategoryCode"],
      subcategoryCode: String(
        data.get("subcategoryCode") ?? "",
      ) as OrganizationIntake["subcategoryCode"],
      jurisdiction: String(data.get("jurisdiction") ?? ""),
      registrationNumber: optional(data.get("registrationNumber")),
      website: optional(data.get("website")),
      requestedRole: String(data.get("requestedRole") ?? ""),
      representativeName: String(data.get("representativeName") ?? ""),
      representativeEmail: String(data.get("representativeEmail") ?? ""),
      declaredRelationship: String(data.get("declaredRelationship") ?? ""),
      requestedCapabilities: data.getAll(
        "requestedCapabilities",
      ) as OrganizationIntake["requestedCapabilities"],
      sourceCubeCode: optional(data.get("sourceCubeCode")),
      driveEvidenceFolderId: driveFolderId(
        optional(data.get("driveEvidenceFolderId")),
      ),
      idempotencyKey: crypto.randomUUID(),
    };
    const parsed = OrganizationIntakeSchema.safeParse(draft);
    if (!parsed.success) {
      setError(
        parsed.error.issues[0]?.message ??
          "Review the organization intake fields.",
      );
      formRef.current
        ?.querySelector<HTMLElement>(
          "input:invalid, select:invalid, textarea:invalid, input",
        )
        ?.focus();
      return;
    }
    setBusy(true);
    try {
      await api.intakeOrganization(token, parsed.data);
      event.currentTarget.reset();
      setSelectedCapabilities(new Set(SAFE_DEFAULTS));
      setPrimaryCategoryCode("");
      toast("Organization intake recorded for verification.", "success");
      onCreated();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "The intake could not be recorded.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="intake-form"
      noValidate
      ref={formRef}
      onSubmit={submit}
      onReset={() => {
        setSelectedCapabilities(new Set(SAFE_DEFAULTS));
        setPrimaryCategoryCode("");
      }}
    >
      <div className="form-heading">
        <div>
          <p className="eyebrow">CONTROLLED PARTICIPATION</p>
          <h2>Organization intake</h2>
          <p className="heading-support">
            Establish identity, evidence, and the requested operating boundary.
          </p>
        </div>
        <span className="status status--pending">Unverified</span>
      </div>

      <ol className="intake-steps" aria-label="Organization onboarding stages">
        {[
          ["01", "Intake", "Current"],
          ["02", "Verify", "Evidence"],
          ["03", "Scope", "Boundaries"],
          ["04", "SETC", "Decision"],
        ].map(([number, title, detail], index) => (
          <li className={index === 0 ? "current" : ""} key={number}>
            <span>{number}</span>
            <div>
              <strong>{title}</strong>
              <small>{detail}</small>
            </div>
          </li>
        ))}
      </ol>

      <p className="boundary-note">
        Intake records a request. It does not establish verification,
        contractual authority, funding, banking access, or permission to
        transact.
      </p>

      {error && (
        <div className="alert alert--error" role="alert">
          {error}
        </div>
      )}

      <fieldset className="form-section">
        <legend>
          <span>01</span>
          <div>
            <strong>Organization identity</strong>
            <small>Use the exact registered entity—not a trade name.</small>
          </div>
        </legend>
        <div className="field-grid">
          <label>
            Legal name <em>Required</em>
            <input
              name="legalName"
              autoComplete="organization"
              placeholder="Registered legal entity name"
              required
            />
          </label>
          <label>
            Organization type <em>Required</em>
            <select name="organizationType" defaultValue="OTHER" required>
              <option value="GOVERNMENT">Government</option>
              <option value="UTILITY">Utility</option>
              <option value="FINANCIAL">Financial institution</option>
              <option value="ACADEMIC">Academic</option>
              <option value="COMMUNITY">Community organization</option>
              <option value="VENDOR">Vendor / EPC provider</option>
              <option value="OTHER">Other / developer</option>
            </select>
          </label>
          <label>
            Primary category <em>Required</em>
            <select
              name="primaryCategoryCode"
              value={primaryCategoryCode}
              onChange={(event) => setPrimaryCategoryCode(event.target.value)}
              required
            >
              <option value="">Select category</option>
              {ORGANIZATION_TAXONOMY.map((category) => (
                <option key={category.code} value={category.code}>
                  {category.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Subcategory <em>Required</em>
            <select
              name="subcategoryCode"
              key={primaryCategoryCode}
              defaultValue=""
              disabled={!primaryCategoryCode}
              required
            >
              <option value="">Select subcategory</option>
              {ORGANIZATION_TAXONOMY.find(
                (category) => category.code === primaryCategoryCode,
              )?.subcategories.map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Ecosystem relationship <em>Required</em>
            <select
              name="relationshipType"
              defaultValue="EXTERNAL_PARTNER"
              required
            >
              <option value="INTERNAL">Internal entity</option>
              <option value="AFFILIATE">Affiliate</option>
              <option value="EXTERNAL_PARTNER">External partner</option>
              <option value="VENDOR">Vendor</option>
              <option value="REGULATOR">Regulator / authority</option>
              <option value="OTHER">Other</option>
            </select>
          </label>
          <label>
            Formation jurisdiction <em>Required</em>
            <input
              name="jurisdiction"
              placeholder="Georgia, United States"
              required
            />
          </label>
          <label>
            Registration number <span>Optional</span>
            <input
              name="registrationNumber"
              placeholder="State or national registry ID"
            />
          </label>
          <label>
            Official website <span>Optional</span>
            <input
              name="website"
              type="url"
              inputMode="url"
              placeholder="https://organization.example"
            />
          </label>
        </div>
      </fieldset>

      <fieldset className="form-section">
        <legend>
          <span>02</span>
          <div>
            <strong>Relationship and representative</strong>
            <small>Identify the proposed role and accountable human.</small>
          </div>
        </legend>
        <div className="field-grid">
          <label>
            Requested ecosystem role <em>Required</em>
            <input
              name="requestedRole"
              placeholder="EPC delivery partner"
              required
            />
          </label>
          <label>
            Authorized representative <em>Required</em>
            <input
              name="representativeName"
              autoComplete="name"
              placeholder="Full legal name"
              required
            />
          </label>
          <label>
            Representative email <em>Required</em>
            <input
              name="representativeEmail"
              type="email"
              autoComplete="email"
              placeholder="name@organization.example"
              required
            />
          </label>
        </div>
        <label className="full-field">
          Declared relationship <em>Required</em>
          <textarea
            className="resize-none"
            name="declaredRelationship"
            rows={4}
            placeholder="Describe the proposed relationship, intended outcomes, and explicit limits of authority."
            required
          />
        </label>
      </fieldset>

      <fieldset className="form-section">
        <legend>
          <span>03</span>
          <div>
            <strong>Evidence and canonical references</strong>
            <small>
              Reference governed records; do not duplicate them here.
            </small>
          </div>
        </legend>
        <div className="field-grid">
          <label>
            SourceCube code <span>When assigned</span>
            <input name="sourceCubeCode" placeholder="Canonical code only" />
            <small className="field-help">
              Leave blank until SourceCube assigns the domain boundary.
            </small>
          </label>
          <label>
            Drive evidence folder <span>Optional at intake</span>
            <input
              name="driveEvidenceFolderId"
              placeholder="Paste a folder URL or ID"
            />
            <small className="field-help">
              SideKick stores only the governed folder reference.
            </small>
          </label>
        </div>
        <div className="evidence-readiness">
          <div>
            <FileCheck2 size={18} />
            <strong>Evidence package readiness</strong>
          </div>
          <ul>
            {[
              "Legal existence",
              "Representative authority",
              "Ownership / KYC",
              "Licensing and insurance",
              "Cyber and data controls",
            ].map((item) => (
              <li key={item}>
                <Check size={13} /> {item}
              </li>
            ))}
          </ul>
        </div>
      </fieldset>

      <fieldset className="form-section">
        <legend>
          <span>04</span>
          <div>
            <strong>Requested capabilities</strong>
            <small>
              Requests remain inactive until explicit scope approval.
            </small>
          </div>
        </legend>
        <div className="capability-grid">
          {CAPABILITIES.map((capability) => (
            <label
              className={`capability-card${"elevated" in capability ? " elevated" : ""}`}
              key={capability.value}
            >
              <input
                name="requestedCapabilities"
                value={capability.value}
                type="checkbox"
                defaultChecked={SAFE_DEFAULTS.has(capability.value)}
                onChange={(event) =>
                  updateCapability(
                    capability.value,
                    event.currentTarget.checked,
                  )
                }
              />
              <span>
                <strong>{capability.label}</strong>
                <small>{capability.description}</small>
              </span>
            </label>
          ))}
        </div>
        {elevatedRequested && (
          <div className="elevated-warning" role="status">
            <ShieldAlert size={17} />
            Execute and administer requests require a named SETC authorization,
            bounded scope, and validity period before activation.
          </div>
        )}
      </fieldset>

      <div className="form-actions form-actions--governed">
        <p>
          Submission creates an immutable intake event and begins verification.
        </p>
        <div>
          <button type="reset" className="button button--ghost" disabled={busy}>
            Clear
          </button>
          <button
            type="submit"
            className="button button--primary"
            disabled={busy}
          >
            {busy ? "Recording…" : "Record for verification"}
          </button>
        </div>
      </div>
    </form>
  );
}

function optional(value: FormDataEntryValue | null) {
  const normalized = String(value ?? "").trim();
  return normalized.length > 0 ? normalized : null;
}

export function driveFolderId(value: string | null) {
  if (!value) return null;
  const match = value.match(/\/folders\/([^/?#]+)/);
  return match?.[1] ?? value;
}
